/**
 * AdminCodeDeliveryPage - UC-027 Project Code Delivery
 *
 * This page allows admins to upload project code ZIP files for delivery.
 * Only projects in "FinishedTesting" status can have code delivered.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Button, Card } from '../components/ui';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';

interface Project {
  projectId: number;
  name: string;
  description: string;
  status: string;
  userId: number;
  creationDate: string;
}

export function AdminCodeDeliveryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      loadProject(parseInt(projectId, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadProject = async (id: number) => {
    try {
      setLoading(true);
      // api.get returns the payload directly — no .data unwrap.
      const response = await api.get<Project>(`/projects/${id}`);
      setProject(response);
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error(t('adminCodeDelivery.loadError', 'Failed to load project details'));
      navigate('/admin/project-list');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Clear previous errors
    setErrorMessage('');

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setErrorMessage(t('adminCodeDelivery.errors.invalidType', 'Please select a valid ZIP file.'));
      return;
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      setErrorMessage(t('adminCodeDelivery.errors.tooLarge', 'File size exceeds the 50MB limit. Please compress your files further.'));
      return;
    }

    setSelectedFile(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setErrorMessage('');

    // Clear the file input
    const fileInput = document.getElementById('zip-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async () => {
    if (!selectedFile || !project) {
      setErrorMessage(t('adminCodeDelivery.errors.noFile', 'Please select a ZIP file to upload.'));
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('zip_file', selectedFile);

      await api.post(`/projects/${project.projectId}/delivery`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(t('adminCodeDelivery.success', 'Code delivered successfully!'));
      navigate(`/admin/projects/${project.projectId}`);
    } catch (error: any) {
      console.error('Error delivering code:', error);
      const errorMsg = error.response?.data?.detail || t('adminCodeDelivery.errors.deliverFailed', 'Failed to deliver the code. Please try again.');
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Clear any uploaded file and navigate back
    setSelectedFile(null);
    setErrorMessage('');
    navigate(`/admin/projects/${project?.projectId}`);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="p-6">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('adminCodeDelivery.notFound.title', 'Project Not Found')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('adminCodeDelivery.notFound.body', 'The requested project could not be found.')}
            </p>
            <Button onClick={() => navigate('/admin/project-list')}>
              {t('adminCodeDelivery.notFound.back', 'Back to Project List')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('adminCodeDelivery.title', 'Deliver Project Code')}
        </h1>
        <p className="mt-2 text-gray-600">
          {t('adminCodeDelivery.subtitle', 'Upload the complete project code as a ZIP file for delivery to the client')}
        </p>
      </div>

      {/* Project Info */}
      <Card className="mb-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{project.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${
              project.status === 'FinishedTesting'
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-gray-50 text-gray-700 border-gray-200'
            }`}>
              {project.status}
            </span>
          </div>
        </div>
      </Card>

      {/* Delivery Section */}
      {project.status === 'FinishedTesting' ? (
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t('adminCodeDelivery.sectionTitle', 'Code Delivery')}
          </h2>

          {/* ZIP Upload Field */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('adminCodeDelivery.zipLabel', 'Project ZIP File')}
            </label>
            <div className="mt-2 flex justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-10 hover:border-blue-500 transition-colors">
              <div className="text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4 flex text-sm text-gray-600">
                  <label
                    htmlFor="zip-upload"
                    className="relative cursor-pointer rounded-md font-semibold text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
                  >
                    <span>{t('adminCodeDelivery.upload', 'Upload ZIP file')}</span>
                    <input
                      id="zip-upload"
                      name="zip-upload"
                      type="file"
                      accept=".zip"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                  </label>
                  <p className="pl-1">{t('adminCodeDelivery.dragDrop', 'or drag and drop')}</p>
                </div>
                <p className="text-xs text-gray-500">
                  {t('adminCodeDelivery.sizeHint', 'ZIP files up to 50MB')}
                </p>
              </div>
            </div>

            {/* Selected File Display */}
            {selectedFile && (
              <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={removeFile}
                    type="button"
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-red-800">
                    {t('adminCodeDelivery.errorTitle', 'Upload Error')}
                  </h4>
                  <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button
              onClick={handleCancel}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedFile || isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting
                ? t('adminCodeDelivery.submitting', 'Delivering…')
                : t('adminCodeDelivery.submit', 'Deliver Code')}
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="p-6 bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-amber-800">
                {t('adminCodeDelivery.notReady.title', 'Project Not Ready for Delivery')}
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                {t(
                  'adminCodeDelivery.notReady.body',
                  'This project must be in FinishedTesting status before code can be delivered.',
                )}{' '}
                {t('adminCodeDelivery.notReady.currentStatus', 'Current status:')} {project.status}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

export default AdminCodeDeliveryPage;
